

import React, { useEffect, useState } from 'react';
import Footer from '../components/Footer';
import ServicesModals from '../components/ServicesModals';
import CareerConsultModal from '../components/CareerConsultModal';
import HRConsultModal from '../components/HRConsultModal';

const Services = () => {
    const [scrollY, setScrollY] = useState(0);
    const [activeService, setActiveService] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState({ title: '', content: '', icon: '' });
    const [showModal, setShowModal] = useState(false);
    const [showHRModal, setShowHRModal] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY);
            const elements = document.querySelectorAll('.fade-in');
            elements.forEach((el, index) => {
                const rect = el.getBoundingClientRect();
                if (rect.top >= 0 && rect.top <= window.innerHeight * 0.8) {
                    setTimeout(() => {
                        el.classList.add('animate-in');
                    }, index * 100);
                }
            });
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const jobSeekerServices = [
        {
            title: 'Resume Crafting Excellence',
            description: 'Customized resume crafting to stand out in competitive markets.',
            icon: '📝',
            details: 'Professional formatting, keyword optimization, and industry-specific tailoring',
            modalContent: (
                <div>
                    <p className="mb-4 text-sm sm:text-base">
                        Our Resume Crafting Excellence service helps you create a standout resume that captures attention in competitive job markets.
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
                        <li>Professional formatting to enhance readability and appeal.</li>
                        <li>Keyword optimization for ATS compatibility.</li>
                        <li>Industry-specific tailoring to align with your career goals.</li>
                    </ul>
                    <p className="font-semibold text-[#0e6d87] text-sm sm:text-base">
                        “A great resume opens doors to new opportunities.” – Navam Team
                    </p>
                </div>
            ),
        },
        {
            title: 'Career Coaching & Prep',
            description: 'Personalized career coaching and interview preparation.',
            icon: '🎯',
            details: 'Mock interviews, confidence building, and strategic career planning',
            modalContent: (
                <div>
                    <p className="mb-4 text-sm sm:text-base">
                        Our Career Coaching & Prep service equips you with the skills and confidence to excel in interviews and plan your career path.
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
                        <li>Mock interviews to simulate real-world scenarios.</li>
                        <li>Confidence-building techniques for impactful communication.</li>
                        <li>Strategic career planning to align with your aspirations.</li>
                    </ul>
                    <p className="font-semibold text-[#0e6d87] text-sm sm:text-base">
                        “Success starts with preparation and confidence.” – Navam Team
                    </p>
                </div>
            ),
        },
        {
            title: 'ATS Profile Optimization',
            description: 'ATS-optimized profiles to boost visibility and interview chances.',
            icon: '🚀',
            details: 'Advanced keyword strategy and digital presence enhancement',
            modalContent: (
                <div>
                    <p className="mb-4 text-sm sm:text-base">
                        Our ATS Profile Optimization service ensures your profile is visible to recruiters by optimizing for Applicant Tracking Systems.
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
                        <li>Advanced keyword strategies to pass ATS filters.</li>
                        <li>Enhanced digital presence for LinkedIn and job platforms.</li>
                        <li>Personalized profile tweaks to maximize interview opportunities.</li>
                    </ul>
                    <p className="font-semibold text-[#0e6d87] text-sm sm:text-base">
                        “Get noticed by the right employers with an optimized profile.” – Navam Team
                    </p>
                </div>
            ),
        },
    ];

    const businessServices = [
        {
            title: 'Strategic HR Consultation',
            description: 'Strategic HR consultation for streamlined talent acquisition.',
            icon: '💼',
            details: 'End-to-end HR strategy design and development',
            modalContent: (
                <div>
                    <p className="mb-4 text-sm sm:text-base">
                        Our Strategic HR Consultation service streamlines your talent acquisition with tailored HR strategies.
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
                        <li>End-to-end HR strategy development for your business.</li>
                        <li>Alignment of HR goals with organizational objectives.</li>
                        <li>Support for seamless execution.</li>
                    </ul>
                    <p className="font-semibold text-[#0e6d87] text-sm sm:text-base">
                        “Strategic HR drives organizational success.” – Navam Team
                    </p>
                </div>
            ),
        },
        {
            title: 'Custom Recruitment Process',
            description: 'Custom recruitment processes to attract top-tier candidates.',
            icon: '👥',
            details: 'Tailored hiring workflows and candidate assessment frameworks',
            modalContent: (
                <div>
                    <p className="mb-4 text-sm sm:text-base">
                        Our Custom Recruitment Process service attracts top talent with tailored hiring workflows.
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
                        <li>Customized hiring workflows to match your needs.</li>
                        <li>Advanced candidate assessment frameworks for quality hires.</li>
                        <li>Streamlined processes to reduce time-to-hire.</li>
                    </ul>
                    <p className="font-semibold text-[#0e6d87] text-sm sm:text-base">
                        “Hire smarter, grow stronger.” – Navam Team
                    </p>
                </div>
            ),
        },
        {
            title: 'Workforce Optimization',
            description: 'Workforce planning and optimization for business growth.',
            icon: '📈',
            details: 'Strategic planning for sustainable organizational development',
            modalContent: (
                <div>
                    <p className="mb-4 text-sm sm:text-base">
                        Our Workforce Optimization service drives growth through strategic workforce planning.
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
                        <li>Strategic workforce planning for scalability.</li>
                        <li>Performance optimization to enhance productivity.</li>
                        <li>Sustainable development strategies for long-term success.</li>
                    </ul>
                    <p className="font-semibold text-[#0e6d87] text-sm sm:text-base">
                        “Optimize your workforce, amplify your growth.” – Navam Team
                    </p>
                </div>
            ),
        },
    ];

    const additionalServices = [
        {
            name: 'Training & Development',
            icon: '🎓',
            color: 'from-blue-500 to-indigo-600',
            modalContent: (
                <div>
                    <p className="mb-4 text-sm sm:text-base">
                        Our Training & Development service equips your team with skills for success through customized programs.
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
                        <li>Tailored training programs for skill enhancement.</li>
                        <li>Leadership development for future-ready teams.</li>
                        <li>Continuous learning support for employee growth.</li>
                    </ul>
                    <p className="font-semibold text-[#0e6d87] text-sm sm:text-base">
                        “Empower your team with knowledge and skills.” – Navam Team
                    </p>
                </div>
            ),
        },
        {
            name: 'HR Generalist Training',
            icon: '📚',
            color: 'from-green-500 to-emerald-600',
            modalContent: (
                <div>
                    <p className="mb-4 text-sm sm:text-base">
                        Our HR Generalist Training service provides comprehensive learning to master essential HR functions.
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
                        <li>In-depth training on HR operations and compliance.</li>
                        <li>Practical skills for recruitment, employee relations, and more.</li>
                        <li>Customized learning paths for career advancement.</li>
                    </ul>
                    <p className="font-semibold text-[#0e6d87] text-sm sm:text-base">
                        “Build a strong foundation in HR expertise.” – Navam Team
                    </p>
                </div>
            ),
        },
    ];

    const openModal = (title, content, icon) => {
        console.log('Opening modal for:', title);
        setModalContent({ title, content, icon });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        console.log('Closing modal');
        setIsModalOpen(false);
        setModalContent({ title: '', content: '', icon: '' });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white relative overflow-hidden">
            {/* Floating Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                    className="absolute top-32 -left-32 w-80 h-80 bg-[#0e6d87]/5 rounded-full blur-3xl"
                    style={{ transform: `translateY(${scrollY * 0.15}px) rotate(${scrollY * 0.1}deg)` }}
                />
                <div
                    className="absolute bottom-32 -right-32 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"
                    style={{ transform: `translateY(${scrollY * -0.2}px) rotate(${scrollY * -0.1}deg)` }}
                />
                <div
                    className="absolute top-1/2 left-1/2 w-96 h-96 bg-indigo-500/3 rounded-full blur-3xl"
                    style={{ transform: `translate(-50%, -50%) translateY(${scrollY * 0.1}px)` }}
                />
            </div>

            <section className="relative py-16 px-4 sm:px-6 lg:px-12">
                <div className="max-w-7xl mx-auto">
                    {/* Enhanced Header */}
                    <header className="text-center mb-20 fade-in">
                        <div className="inline-block mb-6">
                            <span className="px-4 py-2 bg-[#0e6d87]/10 text-[#0e6d87] rounded-full text-sm font-semibold">
                                Comprehensive HR Solutions
                            </span>
                        </div>
                        <h2 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-slate-900 mb-8 leading-tight">
                            Our{' '}
                            <span className="bg-gradient-to-r from-[#0e6d87] via-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                Services
                            </span>
                        </h2>
                        <p className="mt-8 max-w-4xl mx-auto text-lg sm:text-xl lg:text-2xl text-slate-600 leading-relaxed">
                            Navam empowers{' '}
                            <span className="text-[#0e6d87] font-semibold">job seekers</span> and{' '}
                            <span className="text-[#0e6d87] font-semibold">businesses</span> alike with tailored solutions
                            for career success and efficient hiring.
                        </p>
                    </header>

                    {/* Main Services Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-24">
                        {/* Job Seekers Services */}
                        <div className="fade-in">
                            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 lg:p-12 border border-white/20 hover:shadow-3xl transition-all duration-500">
                                <div className="flex items-center mb-8">
                                    <div className="w-16 h-16 bg-gradient-to-r from-[#0e6d87] to-blue-600 rounded-2xl flex items-center justify-center mr-6">
                                        <span className="text-white text-2xl">🌟</span>
                                    </div>
                                    <div>
                                        <h3 className="text-2xl lg:text-3xl font-bold text-slate-900">For Job Seekers</h3>
                                        <p className="text-slate-600 mt-1">Accelerate your career journey</p>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    {jobSeekerServices.map((service, index) => (
                                        <div
                                            key={index}
                                            className="group p-4 rounded-2xl hover:bg-gray-50/50 transition-all duration-300 cursor-pointer"
                                            onMouseEnter={() => setActiveService(`job-${index}`)}
                                            onMouseLeave={() => setActiveService(null)}
                                            onClick={() => openModal(service.title, service.modalContent, service.icon)}
                                        >
                                            <div className="flex items-start space-x-4">
                                                <div className="w-12 h-12 bg-gradient-to-r from-[#0e6d87]/10 to-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                                                    <span className="text-lg">{service.icon}</span>
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-[#0e6d87] transition-colors duration-300">
                                                        {service.title}
                                                    </h4>
                                                    <p className="text-slate-600 mb-2 leading-relaxed">
                                                        {service.description}
                                                    </p>
                                                    <p className="text-sm text-slate-500 italic">
                                                        {service.details}
                                                    </p>
                                                </div>
                                                <div className="w-6 h-6 text-[#0e6d87] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                    <span>→</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-8 pt-6 border-t border-gray-200">
                                    <button
                                        onClick={() => setShowModal(true)}
                                        className="w-full py-4 bg-gradient-to-r from-[#0e6d87] to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg transform hover:scale-[1.02] transition-all duration-300"
                                    >
                                        Start Your Career Transformation
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Business Services */}
                        <div className="fade-in">
                            <div className="bg-gradient-to-br from-[#0e6d87]/5 via-blue-50/50 to-indigo-50/30 rounded-3xl shadow-2xl p-8 lg:p-12 border border-[#0e6d87]/10 hover:shadow-3xl transition-all duration-500">
                                <div className="flex items-center mb-8">
                                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center mr-6">
                                        <span className="text-white text-2xl">🏢</span>
                                    </div>
                                    <div>
                                        <h3 className="text-2xl lg:text-3xl font-bold text-slate-900">For Businesses</h3>
                                        <p className="text-slate-600 mt-1">Scale your organization efficiently</p>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    {businessServices.map((service, index) => (
                                        <div
                                            key={index}
                                            className="group p-4 rounded-2xl hover:bg-white/50 transition-all duration-300 cursor-pointer"
                                            onMouseEnter={() => setActiveService(`business-${index}`)}
                                            onMouseLeave={() => setActiveService(null)}
                                            onClick={() => openModal(service.title, service.modalContent, service.icon)}
                                        >
                                            <div className="flex items-start space-x-4">
                                                <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                                                    <span className="text-lg">{service.icon}</span>
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-[#0e6d87] transition-colors duration-300">
                                                        {service.title}
                                                    </h4>
                                                    <p className="text-slate-600 mb-2 leading-relaxed">
                                                        {service.description}
                                                    </p>
                                                    <p className="text-sm text-slate-500 italic">
                                                        {service.details}
                                                    </p>
                                                </div>
                                                <div className="w-6 h-6 text-[#0e6d87] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                    <span>→</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-8 pt-6 border-t border-white/30">
                                    <button
                                        onClick={() => setShowHRModal(true)}
                                        className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-lg transform hover:scale-[1.02] transition-all duration-300"
                                    >
                                        Transform Your Business HR
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Additional Services Section */}
                    <div className="mb-24 fade-in">
                        <div className="text-center mb-16">
                            <h3 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                                Additional Expertise
                            </h3>
                            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                                Comprehensive HR solutions to support every aspect of your organization
                            </p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 justify-items-center justify-content-center">
                            {additionalServices.map((service, index) => (
                                <div
                                    key={index}
                                    className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 text-center cursor-pointer"
                                    style={{ animationDelay: `${index * 100}ms` }}
                                    onClick={() => openModal(service.name, service.modalContent, service.icon)}
                                >
                                    <div className={`w-16 h-16 bg-gradient-to-r ${service.color} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                                        <span className="text-white text-2xl">{service.icon}</span>
                                    </div>
                                    <h4 className="text-lg font-bold text-slate-900 group-hover:text-[#0e6d87] transition-colors duration-300">
                                        {service.name}
                                    </h4>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Process Section */}
                    <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-2xl fade-in">
                        <div className="text-center mb-12">
                            <h3 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                                Our Process
                            </h3>
                            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                                A systematic approach to delivering exceptional results
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                            {[
                                { step: '01', title: 'Discovery', desc: 'Understanding your unique needs and goals', icon: '🔍' },
                                { step: '02', title: 'Strategy', desc: 'Crafting tailored solutions and roadmaps', icon: '📋' },
                                { step: '03', title: 'Design', desc: 'Executing with precision and expertise', icon: '⚡' },
                                { step: '04', title: 'Optimization', desc: 'Continuous improvement and support', icon: '🎯' },
                            ].map((process, index) => (
                                <div key={index} className="text-center group">
                                    <div className="relative mb-6">
                                        <div className="w-20 h-20 bg-gradient-to-r from-[#0e6d87] to-blue-600 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                                            <span className="text-white text-2xl">{process.icon}</span>
                                        </div>
                                        <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                                            {process.step}
                                        </div>
                                    </div>
                                    <h4 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-[#0e6d87] transition-colors duration-300">
                                        {process.title}
                                    </h4>
                                    <p className="text-slate-600 leading-relaxed">
                                        {process.desc}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* CTA Section */}
                    <div className="text-center mt-20 fade-in">
                        <h3 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                            Explore Our Portfolio
                        </h3>
                        <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
                            Check out our portfolio on LinkedIn to see our projects and professional achievements.
                        </p>
                        <div className="flex justify-center">
                            <a
                                href="https://www.linkedin.com/company/navam-biz-solutions"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-8 py-4 border-2 border-[#0e6d87] text-[#0e6d87] font-semibold rounded-xl hover:bg-[#0e6d87] hover:text-white transition-all duration-300"
                            >
                                View Portfolio
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <ServicesModals
                isOpen={isModalOpen}
                onClose={closeModal}
                title={modalContent.title}
                content={modalContent.content}
                icon={modalContent.icon}
            />
            <CareerConsultModal showModal={showModal} setShowModal={setShowModal} />
            <HRConsultModal showModal={showHRModal} setShowModal={setShowHRModal} />

            <Footer />
        </div>
    );
};

export default Services;